"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

/**
 * @callback UseNavigationPushCallback
 * @param {string} url
 * @param {import("next/dist/shared/lib/app-router-context.shared-runtime").NavigateOptions} [options]
 */

/**
 * @typedef {object} NavigationContextValue
 * @prop {"idle" | "loading"} state
 * @prop {() => void} startLoading
 * @prop {UseNavigationPushCallback} push
 */

/** @type {NavigationContextValue} */
const DEFAULT_VALUES = {
  push: () => {},
  startLoading: () => {},
  state: "idle",
};

const NavigationContext = createContext(DEFAULT_VALUES);

/**
 * @typedef {object} Props
 * @prop {React.ReactNode} children
 */
/**
 * @param {Props} props
 */
export function NavigationProvider({ children }) {
  const [isLoading, setIsLoading] = useState(false);

  const pathname = usePathname();
  const searchParams = useSearchParams();

  const { push: _push } = useRouter();

  useEffect(() => {
    setIsLoading(false);
  }, [pathname, searchParams]);

  const startLoading = useCallback(() => {
    setIsLoading(true);
  }, []);

  const push = useCallback(
    /**
     * @type {UseNavigationPushCallback}
     */
    (url, options) => {
      if (pathname !== url.split("?")[0]) {
        setIsLoading(true);
      }
      _push(url, options);
    },
    [_push, pathname],
  );

  const value = useMemo(() => {
    /** @type {NavigationContextValue} */
    const value = {
      push,
      startLoading,
      state: isLoading ? "loading" : "idle",
    };
    return value;
  }, [isLoading, push, startLoading]);

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
}

/**
 * This hook is an abstraction of the useRouterHook to support additional features such as loading state.
 */
export function useNavigation() {
  return useContext(NavigationContext);
}
